// frontend/packages/business-console/src/contexts/InboxUnreadContext.jsx
//
// Single inbox-unread poll, shared by every surface that wants the count:
// the desktop sidebar badge, the mobile bottom-nav badge, and the Home
// "Needs you" row (docs/design/mobile-console-ux.md §4.1, §5). A context —
// not a bare hook — so the GET fires once every 30s no matter how many
// consumers mount, instead of one interval per consumer.
//
// Polls only while the tab is visible, and stays idle (count 0) until a
// business is resolved so it never fires during the auth flow. Failures are
// swallowed: the count is decorative, never load-bearing.

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { BusinessContext } from "./BusinessContext";
import { getInboxSummary } from "../services/inboxService";

const InboxUnreadContext = createContext(0);

export function InboxUnreadProvider({ children, pollMs = 30000 }) {
  const { business } = useContext(BusinessContext);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!business?._id) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const summary = await getInboxSummary();
        if (!cancelled) setUnread(summary?.unread || 0);
      } catch {
        // Decorative; failure is non-fatal.
      }
    };
    tick();
    timer = setInterval(tick, pollMs);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [business?._id, pollMs]);

  return (
    <InboxUnreadContext.Provider value={unread}>
      {children}
    </InboxUnreadContext.Provider>
  );
}

export const useInboxUnread = () => useContext(InboxUnreadContext);
